import { FunctionComponent, useEffect } from 'react';
import styled from 'styled-components';

import {
  PrimaryButton,
  ImagesBackground,
  SECTION_THEME,
  useDeviceSize,
  ICON_NAME,
  Toast,
} from '@amzn/sss-website-theme';
import imageStyles from '@amzn/sss-website-theme/dist/components/common/images-background/ImagesBackground.module.scss';
import themeVariables from '@amzn/sss-website-theme/dist/theme/variables.module.scss';

import { isRebrandingWeblabEnabled } from 'src/clients/weblab/weblab-client';

import { useThankYouData } from './useThankYouData';

interface ThankYouPageProps {
  toastTitle: string;
  toastBody: string;

  // Text content displayed on the page.
  copy: string;

  // Main title of the page.
  title: string;

  // Label for the primary button.
  cta?: string;

  /**
   * Optional argument to force server side navigation. Used to navigate between pages with different Auth requirements to
   * force Mons Auth check. Default is false.
   */
  forceServerSideNav?: boolean;

  // Optional URL for the primary button if it's a link.
  ctaSrc?: string;
}

const ThankYouContainer = styled.div`
  display: flex !important;
  align-items: center;
  margin-top: 24px;
  flex-direction: column;
  height: 100vh;
  color: ${themeVariables.sustainabilitySpruce};
  background-color: ${themeVariables.lightSpruce};
  padding: 0 16px;

  @media (min-width: ${themeVariables.largeDevice}) {
    margin-top: 0;
    justify-content: center;
  }

  @media (min-width: ${themeVariables.smallDesktop}) {
    padding: 0 60px;
  }
`;

const ThankYouToast = styled(Toast)`
  margin-bottom: 64px;

  @media (min-width: ${themeVariables.largeDevice}) {
    position: absolute;
    top: 96px;
    width: 50vw;
    max-width: 640px;
  }

  @media (min-width: ${themeVariables.smallDesktop}) {
    top: 120px;
  }
`;

const CopyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 72px;
`;

const ThankYouContent = styled.div<{ isFullWidthMobile?: boolean }>`
  text-align: center;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 48px;

  @media (min-width: ${themeVariables.smallDevice}) {
    width: 80%;
  }

  @media (min-width: ${themeVariables.smallDesktop}) {
    width: 100%;
    flex-basis: 100%;
    gap: 32px;
    align-items: center;
  }
`;

const Copy = styled.p`
  margin: 0 !important;
  opacity: 0.9;
  font-size: ${themeVariables.subtitleSize} !important;
  color: ${themeVariables.sustainabilitySpruce};
  line-height: 32px !important;

  @media (min-width: ${themeVariables.smallDesktop}) {
    width: 55%;
    flex-basis: 55%;
  }
`;

const Title = styled.h2`
  color: ${themeVariables.sustainabilitySpruce};
`;

const BackCta = styled(PrimaryButton)`
  font-size: 16px !important;
  color: ${themeVariables.pistachio} !important;
  background: ${themeVariables.sustainabilitySpruce} !important;
`;

export const ThankYouPage: FunctionComponent<ThankYouPageProps> = ({
  toastTitle,
  toastBody,
  copy,
  title,
  cta,
  forceServerSideNav,
  ctaSrc,
}) => {
  const { isSmallDesktop } = useDeviceSize();
  const { images } = useThankYouData(isSmallDesktop);

  useEffect(() => {
    // Scroll to top of the page when the component is mounted
    window.scrollTo(0, 0);
  }, []);

  return (
    <ThankYouContainer>
      {toastTitle && toastBody && <ThankYouToast iconName={ICON_NAME.CHECK} title={toastTitle} body={toastBody} />}
      <CopyContainer>
        <ThankYouContent>
          <Title>{title}</Title>
          <Copy>{copy}</Copy>
        </ThankYouContent>

        {cta && ctaSrc && (
          <BackCta
            link={ctaSrc}
            label={cta}
            theme={SECTION_THEME.Light}
            icon={ICON_NAME.CHEVRON_RIGHT}
            data-testid="backCta"
            forceServerSideNav={forceServerSideNav}
            isRebrandingActive={isRebrandingWeblabEnabled}
          />
        )}
      </CopyContainer>

      <ImagesBackground
        isRebrandingActive={isRebrandingWeblabEnabled}
        images={images}
        className={imageStyles.contactFormImages}
      />
    </ThankYouContainer>
  );
};
